import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import {
  getGetProjectQueryKey,
  getListClientInvoicesQueryKey,
  getListClientReceiptsQueryKey,
  getListClientDocumentsQueryKey,
  getListScInvoicesQueryKey,
  getListScPaymentsQueryKey,
  getListSupplierInvoicesQueryKey,
  getListSupplierPaymentsQueryKey,
  getListVendorOrdersQueryKey,
  getListPayrollQueryKey,
  getListCashFlowQueryKey,
  getListBudgetQueryKey,
  getListBoqQueryKey,
  getListPromissoryNotesQueryKey,
} from "./generated/api";
import { getListProjectMembersQueryKey } from "./projectMembers";

export function getProjectSummaryQueryKey(projectId: string) {
  return [`/api/projects/${projectId}/summary`] as const;
}

export function useProjectSummary(projectId: string) {
  const qc = useQueryClient();

  return useQuery({
    queryKey: getProjectSummaryQueryKey(projectId),
    staleTime: 60_000,
    enabled: !!projectId,
    queryFn: async ({ signal }) => {
      const data = await customFetch<Record<string, unknown>>(
        `/api/projects/${projectId}/summary`,
        { signal, method: "GET" },
      );

      const pid = projectId;
      const cid = (data.project as { companyId?: string } | undefined)?.companyId;

      qc.setQueryData(getGetProjectQueryKey(pid), data.project);

      qc.setQueryData(getListClientInvoicesQueryKey({ projectId: pid }), data.clientInvoices);
      qc.setQueryData(getListClientReceiptsQueryKey({ projectId: pid }), data.clientReceipts);
      qc.setQueryData(getListClientDocumentsQueryKey({ projectId: pid }), data.clientDocuments);
      qc.setQueryData(getListScInvoicesQueryKey({ projectId: pid }), data.scInvoices);
      qc.setQueryData(getListScPaymentsQueryKey({ projectId: pid }), data.scPayments);
      qc.setQueryData(getListSupplierInvoicesQueryKey({ projectId: pid }), data.supplierInvoices);
      qc.setQueryData(getListSupplierPaymentsQueryKey({ projectId: pid }), data.supplierPayments);
      qc.setQueryData(getListVendorOrdersQueryKey({ projectId: pid }), data.vendorOrders);
      qc.setQueryData(getListPayrollQueryKey({ projectId: pid }), data.payroll);
      qc.setQueryData(getListCashFlowQueryKey({ projectId: pid }), data.cashFlow);
      qc.setQueryData(getListBudgetQueryKey({ projectId: pid }), data.budget);
      qc.setQueryData(getListBoqQueryKey({ projectId: pid }), data.boq);
      qc.setQueryData(getListPromissoryNotesQueryKey({ projectId: pid }), data.promissoryNotes);
      qc.setQueryData(getListProjectMembersQueryKey(pid), data.projectMembers);

      if (cid) {
        qc.setQueryData(getListClientDocumentsQueryKey({ projectId: pid, companyId: cid }), data.clientDocuments);
        qc.setQueryData(getListVendorOrdersQueryKey({ projectId: pid, companyId: cid }), data.vendorOrders);
      }

      return data;
    },
  });
}
